import type {Moment} from 'moment-timezone'
import {fromJust, mmap} from '@freckle/maybe'
import {
  formatMoment,
  formatGrade,
} from '@freckle/educator-entities/ts/common/helpers/common-table-helper'
import type {RlDomainT} from '@freckle/educator-entities/ts/common/models/rl-domain'
import type {RlStandardWithDomainT} from '@freckle/educator-entities/ts/common/models/rl-standard'
import type {RlStandardSetT} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import type {DehydratedMathAssessmentTopicT} from '@freckle/educator-entities/ts/math/assessment/types/assessment-topic'
import * as MathAssessmentTopic from '@freckle/educator-entities/ts/math/assessment/types/assessment-topic'
import type {BaseTeacherAttrs} from '@freckle/educator-entities/ts/users/models/teacher'
import {formattedName} from '@freckle/educator-entities/ts/users/models/teacher'
import {Label} from '@freckle/educator-materials/ts/components/label'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {Table, cellFromRow, aligned} from '@freckle/educator-materials/ts/components/table'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {Routes} from '@freckle/school/ts/common/routers/routes'
import type {MathSchoolAssessmentAssignmentT} from '@freckle/school/ts/pages/assessments/models/math-school-assessment-assignment'
import {tableWrapper} from '../assessments.module.scss'

type Props = {
  assessments: Array<MathSchoolAssessmentAssignmentT>
  schoolId: number
  teachers: Array<BaseTeacherAttrs>
  mathStandardSet: RlStandardSetT
  domains: Array<RlDomainT>
  standards: Array<RlStandardWithDomainT>
}

export default function AssessmentsTable(props: Props) {
  const {t} = useSafeTranslation()
  const {assessments, teachers, schoolId, mathStandardSet, domains, standards} = props

  const rows = assessments.flatMap(assessment => {
    return assessment.teacherIds.map((teacherId, i: number) => {
      const shouldRowBePopulated = i === 0
      const assignmentAttribute = shouldRowBePopulated
        ? {
            id: assessment.id,
            createdAt: assessment.createdAt,
            grade: mmap(topic => topic.schoolGrade, assessment.topic),
            topic: assessment.topic,
          }
        : {
            id: assessment.id,
            createdAt: null,
            grade: null,
            topic: null,
          }

      const teacher = fromJust(
        teachers.find(teacher => teacher.id === teacherId),
        `Could not find teacher with id ${teacherId}`,
      )
      return {
        ...assignmentAttribute,
        teacherId: teacher.id,
        teacherName: formattedName(teacher),
        customId: `${assessment.id}-${teacher.id}`,
      }
    })
  })

  return (
    <div className={tableWrapper}>
      <Table
        data={rows}
        columns={[
          {
            Header: <Label>{t('ASSESSMENTS_PAGE_TABLE_DATE_COLUMN_TEXT')}</Label>,
            accessor: (d: RowT) => d.createdAt,
            Cell: cellFromRow(({createdAt}) => <>{formatMoment('L', createdAt)}</>),
            id: 'createdAt',
            widthPx: 150,
            ...aligned('left'),
          },
          {
            Header: <Label>{t('ASSESSMENTS_PAGE_TABLE_DATE_COLUMN_TITLE')}</Label>,
            accessor: (d: RowT) => d.grade,
            Cell: cellFromRow(({grade}) => <>{formatGrade(grade, t)}</>),
            id: 'grade',
            widthPx: 150,
            ...aligned('left'),
          },
          {
            Header: <Label>{t('ASSESSMENTS_PAGE_TABLE_DOMAINS_COLUMN_TITLE')}</Label>,
            accessor: (d: RowT) => d.topic,
            Cell: cellFromRow(row => (
              <Topic
                row={row}
                allDomains={domains}
                allStandards={standards}
                mathStandardSet={mathStandardSet}
              />
            )),
            id: 'topic',
            widthPx: 300,
            ...aligned('left'),
          },
          {
            Header: <Label>{t('ASSESSMENTS_PAGE_TABLE_TEACHER_COLUMN_TITLE')}</Label>,
            accessor: (d: RowT) => d.teacherName,
            Cell: cellFromRow(({teacherName}) => <>{teacherName}</>),
            id: 'teacherName',
            widthPx: 200,
            ...aligned('left'),
          },
          {
            Cell: cellFromRow(row => <Action row={row} schoolId={schoolId} />),
            id: 'action',
            widthPx: 200,
            alignment: 'right',
          },
        ]}
      />
    </div>
  )
}

type RowT = {
  id: number
  createdAt: Moment | undefined | null
  grade: number | undefined | null
  topic: DehydratedMathAssessmentTopicT | undefined | null
  teacherId: number
  teacherName: string
  customId: string
}

type ExtraDataT = {
  allDomains: Array<RlDomainT>
  allStandards: Array<RlStandardWithDomainT>
  mathStandardSet: RlStandardSetT
}

const EMDASH = '—'

const Topic = ({
  row: {topic},
  allDomains,
  allStandards,
  mathStandardSet,
}: {row: RowT} & ExtraDataT) => {
  const {t} = useSafeTranslation()
  if (topic === null || topic === undefined) {
    return <>{EMDASH}</>
  }
  return <>{MathAssessmentTopic.format(topic, mathStandardSet, allDomains, allStandards, t)}</>
}

const Action = ({row, schoolId}: {row: RowT; schoolId: number}) => {
  const {t} = useSafeTranslation()
  return (
    <Link href={Routes.schools.assessment(schoolId, row.id, row.teacherId)} reactRouter>
      {t('ASSESSMENTS_PAGE_TABLE_SEE_DETAILS_TEXT')}
    </Link>
  )
}
